import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../../hooks/useTheme";

interface ServiceItem {
	icon: string;
	nameKey: string;
	priceKey: string;
	descKey: string;
}

interface ServiceCategory {
	id: string;
	labelKey: string;
	icon: string;
	color: string;
	items: ServiceItem[];
}

const CATEGORIES: ServiceCategory[] = [
	{
		id: "operacion",
		labelKey: "addsvc_cat1",
		icon: "ri-team-line",
		color: "#00cfff",
		items: [
			{
				icon: "ri-user-settings-line",
				nameKey: "addsvc_a1_n",
				priceKey: "addsvc_a1_p",
				descKey: "addsvc_a1_d",
			},
			{
				icon: "ri-user-star-line",
				nameKey: "addsvc_a2_n",
				priceKey: "addsvc_a2_p",
				descKey: "addsvc_a2_d",
			},
			{
				icon: "ri-computer-line",
				nameKey: "addsvc_a3_n",
				priceKey: "addsvc_a3_p",
				descKey: "addsvc_a3_d",
			},
			{
				icon: "ri-store-2-line",
				nameKey: "addsvc_a4_n",
				priceKey: "addsvc_a4_p",
				descKey: "addsvc_a4_d",
			},
		],
	},
	{
		id: "industria",
		labelKey: "addsvc_cat2",
		icon: "ri-apps-2-line",
		color: "#a855f7",
		items: [
			{
				icon: "ri-restaurant-2-line",
				nameKey: "addsvc_b3_n",
				priceKey: "addsvc_b3_p",
				descKey: "addsvc_b3_d",
			},
			{
				icon: "ri-truck-line",
				nameKey: "addsvc_b4_n",
				priceKey: "addsvc_b4_p",
				descKey: "addsvc_b4_d",
			},
			{
				icon: "ri-car-line",
				nameKey: "addsvc_b5_n",
				priceKey: "addsvc_b5_p",
				descKey: "addsvc_b5_d",
			},
			{
				icon: "ri-building-2-line",
				nameKey: "addsvc_b6_n",
				priceKey: "addsvc_b6_p",
				descKey: "addsvc_b6_d",
			},
			{
				icon: "ri-stethoscope-line",
				nameKey: "addsvc_b7_n",
				priceKey: "addsvc_b7_p",
				descKey: "addsvc_b7_d",
			},
			{
				icon: "ri-hammer-line",
				nameKey: "addsvc_b8_n",
				priceKey: "addsvc_b8_p",
				descKey: "addsvc_b8_d",
			},
		],
	},
	{
		id: "automatizacion",
		labelKey: "addsvc_cat3",
		icon: "ri-flow-chart",
		color: "#6366f1",
		items: [
			{
				icon: "ri-whatsapp-line",
				nameKey: "addsvc_c1_n",
				priceKey: "addsvc_c1_p",
				descKey: "addsvc_c1_d",
			},
			{
				icon: "ri-robot-2-line",
				nameKey: "addsvc_c2_n",
				priceKey: "addsvc_c2_p",
				descKey: "addsvc_c2_d",
			},
			{
				icon: "ri-code-s-slash-line",
				nameKey: "addsvc_c3_n",
				priceKey: "addsvc_c3_p",
				descKey: "addsvc_c3_d",
			},
		],
	},
	{
		id: "soporte",
		labelKey: "addsvc_cat4",
		icon: "ri-customer-service-2-line",
		color: "#0ea5e9",
		items: [
			{
				icon: "ri-shield-flash-line",
				nameKey: "addsvc_d1_n",
				priceKey: "addsvc_d1_p",
				descKey: "addsvc_d1_d",
			},
			{
				icon: "ri-graduation-cap-line",
				nameKey: "addsvc_d2_n",
				priceKey: "addsvc_d2_p",
				descKey: "addsvc_d2_d",
			},
			{
				icon: "ri-database-2-line",
				nameKey: "addsvc_d3_n",
				priceKey: "addsvc_d3_p",
				descKey: "addsvc_d3_d",
			},
			{
				icon: "ri-terminal-box-line",
				nameKey: "addsvc_d4_n",
				priceKey: "addsvc_d4_p",
				descKey: "addsvc_d4_d",
			},
		],
	},
];

interface ServiceCardProps {
	item: ServiceItem;
	color: string;
	isLight: boolean;
	index: number;
	t: (key: string) => string;
}

function ServiceCard({ item, color, isLight, index, t }: ServiceCardProps) {
	const [hovered, setHovered] = useState(false);

	return (
		<div
			className="flex flex-col rounded-2xl border p-4 md:p-5 transition-all duration-300 animate-fadeIn"
			style={{
				borderColor: hovered
					? color + (isLight ? "70" : "55")
					: isLight
						? "rgba(10,22,40,0.10)"
						: "rgba(255,255,255,0.07)",
				background: isLight
					? hovered
						? `linear-gradient(135deg, ${color}0f 0%, #ffffff 70%)`
						: "#ffffff"
					: hovered
						? `linear-gradient(135deg, ${color}12 0%, rgba(10,20,40,0.95) 70%)`
						: "rgba(10,20,40,0.6)",
				boxShadow: hovered
					? isLight
						? `0 8px 24px ${color}18`
						: `0 8px 30px ${color}1a`
					: "none",
				transform: hovered ? "translateY(-3px)" : "translateY(0)",
				animationDelay: `${index * 50}ms`,
			}}
			onMouseEnter={() => setHovered(true)}
			onMouseLeave={() => setHovered(false)}
		>
			{/* Header: icon + name */}
			<div className="flex items-start gap-3 mb-2.5">
				<div
					className="w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0 transition-colors duration-300"
					style={{
						backgroundColor: color + (hovered ? "26" : "18"),
						color: color,
					}}
				>
					<i className={`${item.icon} text-lg`}></i>
				</div>
				<h4
					className="font-heading text-sm font-bold leading-snug pt-0.5"
					style={{ color: isLight ? "#0a1628" : "#ffffff" }}
				>
					{t(item.nameKey)}
				</h4>
			</div>

			{/* Description */}
			<p
				className="font-body text-xs leading-relaxed flex-1 mb-4"
				style={{
					color: isLight ? "rgba(10,22,40,0.60)" : "rgba(255,255,255,0.55)",
				}}
			>
				{t(item.descKey)}
			</p>

			{/* Price */}
			<div
				className="flex items-center gap-2 pt-3 border-t"
				style={{
					borderColor: isLight
						? "rgba(10,22,40,0.08)"
						: "rgba(255,255,255,0.08)",
				}}
			>
				<div
					className="w-6 h-6 flex items-center justify-center rounded-lg flex-shrink-0"
					style={{ backgroundColor: color + "18", color: color }}
				>
					<i className="ri-price-tag-3-line text-xs"></i>
				</div>
				<span
					className="font-heading text-base font-extrabold leading-none"
					style={{ color: color }}
				>
					{t(item.priceKey)}
				</span>
			</div>
		</div>
	);
}

export default function AdditionalServices() {
	const [activeTab, setActiveTab] = useState(CATEGORIES[0].id);
	const { t } = useTranslation();
	const { theme } = useTheme();
	const isLight = theme === "light";

	const current = CATEGORIES.find((c) => c.id === activeTab)!;

	return (
		<section
			id="servicios-adicionales"
			className="bg-navy-950 py-12 md:py-24 px-4 sm:px-5 md:px-6 relative overflow-hidden"
		>
			{/* Decorative bg glow */}
			<div
				className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[700px] h-[400px] rounded-full pointer-events-none"
				style={{
					background: `radial-gradient(ellipse, ${current.color}08 0%, transparent 70%)`,
					filter: "blur(60px)",
					transition: "background 0.5s ease",
				}}
			/>

			<div className="max-w-6xl mx-auto relative z-10">
				{/* Header */}
				<div className="text-center mb-9 md:mb-12">
					<span className="inline-block text-electric text-xs md:text-sm font-semibold tracking-widest uppercase font-body mb-2 md:mb-3">
						{t("addsvc_label")}
					</span>
					<h2 className="font-heading text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-3 md:mb-4">
						{t("addsvc_title")}
					</h2>
					<p className="font-body text-white/60 text-sm md:text-base max-w-xl mx-auto">
						{t("addsvc_intro")}
					</p>
				</div>

				{/* Tabs */}
				<div className="flex flex-wrap justify-center gap-2 md:gap-3 mb-7 md:mb-10">
					{CATEGORIES.map((cat) => {
						const isActive = cat.id === activeTab;
						return (
							<button
								key={cat.id}
								onClick={() => setActiveTab(cat.id)}
								className="flex items-center gap-2 px-4 md:px-5 py-2.5 rounded-full text-xs md:text-sm font-semibold font-body transition-all duration-250 cursor-pointer whitespace-nowrap"
								style={{
									backgroundColor: isActive
										? cat.color + "18"
										: isLight
											? "rgba(10,22,40,0.04)"
											: "rgba(255,255,255,0.04)",
									border: `1.5px solid ${
										isActive
											? cat.color + "60"
											: isLight
												? "rgba(10,22,40,0.10)"
												: "rgba(255,255,255,0.08)"
									}`,
									color: isActive
										? cat.color
										: isLight
											? "rgba(10,22,40,0.50)"
											: "rgba(255,255,255,0.50)",
									transform: isActive ? "translateY(-2px)" : "translateY(0)",
									boxShadow: isActive ? `0 4px 20px ${cat.color}18` : "none",
								}}
							>
								<div className="w-4 h-4 flex items-center justify-center">
									<i className={`${cat.icon} text-sm`}></i>
								</div>
								{t(cat.labelKey)}
							</button>
						);
					})}
				</div>

				{/* Active tab panel */}
				<div
					key={activeTab}
					className="relative rounded-3xl border p-4 sm:p-6 md:p-8"
					style={{
						borderColor: isLight
							? current.color + "30"
							: current.color + "20",
						background: isLight
							? `linear-gradient(145deg, ${current.color}0d 0%, rgba(248,251,255,0.98) 60%)`
							: `linear-gradient(145deg, ${current.color}0a 0%, rgba(6,15,35,0.95) 60%)`,
					}}
				>
					{/* Panel header */}
					<div className="flex items-center gap-3 md:gap-4 mb-5 md:mb-7">
						<div
							className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-xl md:rounded-2xl flex-shrink-0"
							style={{
								backgroundColor: current.color + "18",
								color: current.color,
							}}
						>
							<i className={`${current.icon} text-xl md:text-2xl`}></i>
						</div>
						<h3
							className="font-heading text-base md:text-xl font-bold"
							style={{ color: current.color }}
						>
							{t(current.labelKey)}
						</h3>
					</div>

					{/* Cards grid */}
					<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
						{current.items.map((item, i) => (
							<ServiceCard
								key={item.nameKey}
								item={item}
								color={current.color}
								isLight={isLight}
								index={i}
								t={t}
							/>
						))}
					</div>
				</div>

				{/* Bottom note */}
				<div className="mt-6 md:mt-8 flex items-start justify-center gap-2.5 max-w-3xl mx-auto">
					<div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5 text-electric/70">
						<i className="ri-information-line text-sm"></i>
					</div>
					<p
						className="font-body text-xs leading-relaxed text-center"
						style={{
							color: isLight ? "rgba(10,22,40,0.50)" : "rgba(255,255,255,0.45)",
						}}
					>
						{t("addsvc_note")}
					</p>
				</div>
			</div>
		</section>
	);
}
